import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import { OfficeAppIcon, type OfficeApp } from '@/components/landing/OfficeAppIcon'
import { BRAND, OUT_EASE, halftone } from './halftone'

export interface DiagramNode {
  label: string
  app?: OfficeApp
}

/** Satellite placements per layout, in % of the square: [left, top, size]. */
const LAYOUTS: Array<Array<[number, number, number]>> = [
  [
    [63, 9, 20],
    [11, 63, 17],
    [71, 68, 13],
  ],
  [
    [9, 11, 17],
    [72, 35, 19],
    [38, 74, 16],
  ],
  [
    [57, 7, 19],
    [7, 50, 13],
    [68, 64, 17],
  ],
]

const RINGS = [38, 48, 58, 68]

/**
 * Brand diagram for a solution step: a grey square with concentric outlines,
 * the team's blue tile in the centre and pink satellite tiles for the Office
 * apps and data sources involved. Sized in container units so it scales with
 * the stage. Satellites settle in once, the first time the step is active.
 */
export function TeamDiagram({
  teamName,
  icon: Icon,
  nodes,
  layout = 0,
  active,
}: {
  teamName: string
  icon: LucideIcon
  nodes: DiagramNode[]
  layout?: number
  active: boolean
}) {
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    if (active) setSeen(true)
  }, [active])
  const spots = LAYOUTS[layout % LAYOUTS.length]!

  return (
    <div
      aria-hidden
      className="relative h-full w-full overflow-hidden bg-[#f3f3f3] [container-type:inline-size] dark:bg-[#15151d]"
    >
      {RINGS.map((size) => (
        <span
          key={size}
          className="absolute border border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)]"
          style={{ left: `${50 - size / 2}%`, top: `${50 - size / 2}%`, width: `${size}%`, height: `${size}%` }}
        />
      ))}

      {/* Team tile */}
      <div
        className="absolute flex flex-col justify-between p-[2.4cqw] text-white shadow-[0_24px_48px_-16px_rgba(12,0,255,0.45)]"
        style={{ left: '33%', top: '33%', width: '34%', height: '34%', backgroundColor: BRAND.blue }}
      >
        <span className="absolute inset-0" style={halftone(0.22, 5)} />
        <Icon className="relative h-[6cqw] w-[6cqw]" strokeWidth={1.75} />
        <span className="relative font-[family-name:var(--font-landing-display)] text-[2.7cqw] uppercase leading-[1.35]">
          {teamName}
        </span>
      </div>

      {/* Satellites */}
      {nodes.slice(0, spots.length).map((node, i) => {
        const [left, top, size] = spots[i]!
        return (
          <motion.div
            key={`${node.label}-${i}`}
            className="absolute flex flex-col justify-between p-[1.4cqw] text-white shadow-[0_18px_36px_-14px_rgba(10,10,10,0.4)]"
            style={{ left: `${left}%`, top: `${top}%`, width: `${size}%`, height: `${size}%`, backgroundColor: BRAND.pink }}
            initial={false}
            animate={seen ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.86, y: 14 }}
            transition={{ duration: 0.6, ease: OUT_EASE, delay: seen ? 0.4 + i * 0.09 : 0 }}
          >
            <span className="absolute inset-0" style={halftone(0.25, 5)} />
            {node.app ? (
              <span className="relative flex h-[46%] w-[46%] items-center justify-center bg-white">
                <OfficeAppIcon app={node.app} className="h-[86%] w-[86%]" />
              </span>
            ) : (
              <span className="relative block h-[0.6cqw] w-[38%] bg-white/80" />
            )}
            <span
              className={cn(
                // Labels never break mid-word; long ones (POWERPOINT) step down.
                'relative whitespace-nowrap font-[family-name:var(--font-landing-subdisplay)] font-bold uppercase leading-[1.05] tracking-[-0.01em]',
                node.label.length > 9
                  ? 'text-[max(7px,1.15cqw)]'
                  : node.label.length > 7
                    ? 'text-[max(8px,1.4cqw)]'
                    : 'text-[max(9px,1.9cqw)]'
              )}
            >
              {node.label}
            </span>
          </motion.div>
        )
      })}
    </div>
  )
}

export default TeamDiagram
